import { useEffect, useMemo, useRef, useState } from "react";
import UpdateJobStages from "@components/dashboard/UpdateJobStages";
import { useProjectActions } from "@contexts/projects";
import {
  createProjectTitleAnnulments,
  previewProjectTitleAnnulments,
  revokeProjectTitleAnnulment,
} from "@contexts/projects/titleAnnulmentsApi";
import { brl, titleCount, totalsOf, PENDING_BANNER_TEXT } from "./summary";

// Barra lateral "Títulos anulados na Ampli" (design D6). Mesmas classes e mesma tabela do
// `CostOriginTitlesOffcanvas` (.offcanvas > .offcanvas-container.large.cost-origin-titles-surface,
// estilos inline nas células). Três seções: resumo, "Anular títulos" (só com `canWrite`) e a
// tabela dos anulados, com "Reincluídos (n)" ao final. Depois de salvar mostra o andamento da
// atualização do financeiro. Não usar <span> para texto: o reset de `.card` não vale aqui, mas
// o padrão do app é <p>/<td>.

export const REASON_MIN = 10;
export const REASON_MAX = 500;
export const MAX_NUMBERS = 50;

export const CATEGORY_LABELS = {
  erp_forecast: "Previsão do ERP (PRV)",
  duplicate: "Duplicado",
  out_of_scope: "Fora do escopo da análise",
  other: "Outro motivo",
};

export const STATUS_LABELS = {
  found: "Encontrado",
  not_found: "Não encontrado na última medição",
  already_annulled: "Já anulado",
  ambiguous: "Ambíguo: escolha a empresa",
};

const SKIPPED_LABELS = {
  already_annulled: "já anulado",
  not_found: "não encontrado na última medição",
  ambiguous: "empresa não escolhida",
  invalid_bill_id: "número inválido",
  too_many_titles: "acima do limite de 50 por vez",
};

const ORIGIN_LABELS = { ampli_obras: "Ampli Obras", backoffice: "Backoffice", director: "Diretoria" };

const th = { textAlign: "left", padding: "8px 10px", borderBottom: "2px solid #e0e0e0", fontWeight: 600, whiteSpace: "nowrap" };
const thR = { ...th, textAlign: "right" };
const td = { padding: "8px 10px", borderBottom: "1px solid #eee", verticalAlign: "top" };
const num = { ...td, textAlign: "right", whiteSpace: "nowrap", fontVariantNumeric: "tabular-nums" };

const isMega = (erp) => String(erp || "").toUpperCase() === "MEGA";
const erpLabel = (erp) => (isMega(erp) ? "MEGA" : "Sienge");

const civil = (value) => {
  if (typeof value !== "string" || value.length < 10) return "—";
  const [y, m, d] = value.slice(0, 10).split("-");
  return y && m && d ? `${d}/${m}/${y}` : "—";
};

export const parseNumbers = (text) => [...new Set(
  String(text || "").split(/[\s,;]+/).map((value) => value.trim()).filter(Boolean),
)];

const validReason = (value) => {
  const { length } = String(value || "").trim();
  return length >= REASON_MIN && length <= REASON_MAX;
};

const buildingName = (building) => (
  typeof building === "string" ? building : building?.buildingName ?? building?.name ?? building?.buildingId ?? ""
);

const buildingList = (title) => (Array.isArray(title?.buildings) ? title.buildings : [])
  .map(buildingName).filter(Boolean).join(", ");

// Hora do desbloqueio: "14h" (ou "14h30"), no fuso de Brasília.
export const formatRetryTime = (value) => {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  const parts = Object.fromEntries(
    new Intl.DateTimeFormat("pt-BR", {
      timeZone: "America/Sao_Paulo", hour: "2-digit", minute: "2-digit", hourCycle: "h23",
    }).formatToParts(date).map(({ type, value: part }) => [type, part]),
  );
  return parts.minute === "00" ? `${Number(parts.hour)}h` : `${Number(parts.hour)}h${parts.minute}`;
};

// `update` = { queued | blocked | alreadyRunning | skipped } (D2). Aceita tanto o formato de
// chaves quanto `{ status }`.
export const updateOutcome = (update) => {
  if (!update || typeof update !== "object") return { kind: "none" };
  const kinds = ["queued", "blocked", "alreadyRunning", "skipped"];
  const kind = typeof update.status === "string" && kinds.includes(update.status)
    ? update.status
    : kinds.find((key) => update[key]);
  if (!kind) return { kind: "none" };
  const detail = typeof update[kind] === "object" && update[kind] !== null ? update[kind] : {};
  return {
    kind,
    job: update.job ?? detail.job ?? null,
    retryAfter: update.retryAfter ?? detail.retryAfter ?? null,
  };
};

const updateMessage = ({ kind, retryAfter }) => {
  switch (kind) {
    case "queued":
      return null;
    case "blocked": {
      const time = formatRetryTime(retryAfter);
      return `A obra continua com a atualização do financeiro pendente. ${time ? `O financeiro poderá ser atualizado a partir das ${time}.` : "Tente atualizar o financeiro mais tarde."}`;
    }
    case "alreadyRunning":
      return "Já há uma atualização em andamento nesta construtora. A obra continua com a atualização do financeiro pendente: atualize o financeiro quando ela terminar.";
    case "skipped":
      return "A atualização do financeiro não foi disparada. A obra continua com a atualização do financeiro pendente.";
    default:
      return null;
  }
};

const errorText = (result) => (
  result?.body?.message || (result?.status === 403
    ? "Você não tem permissão para alterar a lista."
    : "Não foi possível salvar. Tente de novo.")
);

const candidateLabel = (candidate) => (
  `${candidate.legalEntityName || `Empresa ${candidate.legalEntityId}`}${candidate.creditorName ? ` · ${candidate.creditorName}` : ""}${candidate.billId != null ? ` · ${candidate.billId}` : ""}`
);

// `thisBuilding` é a parte que saiu desta obra (título rateado); sem ele vale o item todo.
const itemTotals = (item) => {
  const src = item.thisBuilding ?? item;
  const toPay = Number(src.removedToPay ?? item.effect?.removedToPay) || 0;
  const paid = Number(src.removedPaid ?? item.effect?.removedPaid) || 0;
  const total = src.removedTotal != null && Number.isFinite(Number(src.removedTotal)) ? Number(src.removedTotal) : toPay + paid;
  return { toPay, paid, total };
};

const numberCell = (item, mega) => {
  const shown = item.titleNumber ?? item.billId ?? "—";
  return mega && item.billId != null ? `${shown} · AP ${item.billId}` : String(shown);
};

const personOf = (value) => value?.name || "Usuário";

const TitleAnnulmentsOffcanvas = ({ open, onClose, projectId, status, data, project, erp: erpProp, reload }) => {
  const { getUpdateJob, selectProject } = useProjectActions() ?? {};
  const [numbers, setNumbers] = useState("");
  const [legalEntityId, setLegalEntityId] = useState("");
  const [preview, setPreview] = useState(null);
  const [included, setIncluded] = useState({});
  const [chosen, setChosen] = useState({});
  // Prévia refeita para a empresa escolhida de cada linha ambígua: { [índice]: { value, title } }.
  const [resolved, setResolved] = useState({});
  const [category, setCategory] = useState("erp_forecast");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState(null);
  const [revoking, setRevoking] = useState(null);
  const [stages, setStages] = useState(null);

  useEffect(() => {
    if (!open) return undefined;
    const handleEscape = (event) => {
      if (event.key !== "Escape") return;
      event.preventDefault();
      onClose?.();
    };
    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, [open, onClose]);

  // Foco: ao abrir vai para o diálogo e, ao fechar, volta para quem abriu. Tab fica dentro dele.
  const dialogRef = useRef(null);
  useEffect(() => {
    if (!open) return undefined;
    const opener = document.activeElement;
    dialogRef.current?.focus();
    return () => {
      if (opener && typeof opener.focus === "function" && document.contains(opener)) opener.focus();
    };
  }, [open]);

  const trapTab = (event) => {
    if (event.key !== "Tab" || !dialogRef.current) return;
    const focusable = [...dialogRef.current.querySelectorAll(
      'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), summary, [tabindex]:not([tabindex="-1"])',
    )];
    if (focusable.length === 0) {
      event.preventDefault();
      return;
    }
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    const active = document.activeElement;
    if (event.shiftKey && (active === first || active === dialogRef.current)) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && active === last) {
      event.preventDefault();
      first.focus();
    }
  };

  // Outra obra: formulário e andamento começam do zero.
  useEffect(() => {
    setNumbers("");
    setPreview(null);
    setIncluded({});
    setChosen({});
    setResolved({});
    setReason("");
    setMessage(null);
    setRevoking(null);
    setStages(null);
    setLegalEntityId("");
  }, [projectId]);

  const canWrite = data?.canWrite === true;
  const items = Array.isArray(data?.items) ? data.items : [];
  const revoked = Array.isArray(data?.revoked) ? data.revoked : [];
  const legalEntities = Array.isArray(data?.legalEntities) ? data.legalEntities : [];
  const erp = data?.erp ?? erpProp ?? items[0]?.erp ?? project?.erp;
  const mega = isMega(erp);
  const entityValue = legalEntityId
    || (data?.defaultLegalEntityId != null ? String(data.defaultLegalEntityId) : "")
    || (legalEntities.length === 1 ? String(legalEntities[0].id) : "");
  const refreshAfterJob = () => {
    reload?.();
    if (projectId) selectProject?.(projectId);
  };
  const summary = data?.summary ?? project?.titleAnnulments ?? null;
  const totals = useMemo(() => totalsOf(summary), [summary]);
  const parsed = parseNumbers(numbers);
  const tooMany = parsed.length > MAX_NUMBERS;
  // `pending: null` explícito na lista carregada vale: a lista é mais nova que o resumo da obra.
  const pending = data && Object.prototype.hasOwnProperty.call(data, "pending")
    ? data.pending
    : project?.titleAnnulments?.pending ?? null;

  // Candidato escolhido + título da prévia refeita para a empresa (traz billId e valores). A
  // prévia ambígua não traz billId; sem ela, no Sienge o número digitado é o billId.
  const ambiguousTitle = (row, index, picked) => {
    if (!picked) return null;
    const refined = resolved[index]?.value === String(chosen[index]) ? resolved[index].title : null;
    const typed = Number(row.input);
    const billId = refined?.billId ?? picked.billId ?? (!mega && Number.isFinite(typed) ? typed : undefined);
    if (billId === undefined) return null;
    return { ...picked, ...refined, legalEntityId: refined?.legalEntityId ?? picked.legalEntityId, billId };
  };

  const pickCandidate = async (row, index, value) => {
    setChosen((current) => ({ ...current, [index]: value }));
    if (value === "") return;
    const candidate = (Array.isArray(row.candidates) ? row.candidates : [])[Number(value)];
    if (!candidate || candidate.legalEntityId == null) return;
    const result = await previewProjectTitleAnnulments(projectId, {
      numbers: [row.input], legalEntityId: Number(candidate.legalEntityId),
    });
    const found = result?.ok ? (result.body?.results ?? []).find((entry) => entry?.status === "found" && entry.title) : null;
    if (found) setResolved((current) => ({ ...current, [index]: { value, title: found.title } }));
  };

  const selectable = (row, index) => {
    if (!row || included[index] === false) return null;
    if (row.status === "found") return row.title ?? null;
    if (row.status === "ambiguous") {
      const candidates = Array.isArray(row.candidates) ? row.candidates : [];
      return ambiguousTitle(row, index, candidates[Number(chosen[index])]);
    }
    return null;
  };
  const results = preview?.results ?? [];
  const toSend = results.map(selectable).filter(Boolean);
  const reasonOk = validReason(reason);
  const canConfirm = canWrite && !busy && toSend.length > 0 && reasonOk;

  const lookup = async (event) => {
    event.preventDefault();
    if (!parsed.length || tooMany || busy) return;
    setBusy(true);
    setMessage(null);
    const body = { numbers: parsed };
    if (entityValue !== "" && Number.isFinite(Number(entityValue))) body.legalEntityId = Number(entityValue);
    const result = await previewProjectTitleAnnulments(projectId, body);
    setBusy(false);
    if (result?.ok) {
      setPreview(result.body || { results: [] });
      setIncluded({});
      setChosen({});
      setResolved({});
    } else {
      setPreview(null);
      setMessage({ tone: "error", text: errorText(result) });
    }
  };

  const finish = (body, successText) => {
    const outcome = updateOutcome(body?.update);
    const skipped = Array.isArray(body?.skipped) ? body.skipped : [];
    const skippedText = skipped.length
      ? ` Ficaram de fora: ${skipped.map((entry) => `${entry.billId} (${SKIPPED_LABELS[entry.reason] || entry.reason})`).join(", ")}.`
      : "";
    const extra = updateMessage(outcome);
    setMessage({ tone: "success", text: `${successText}${skippedText}${extra ? ` ${extra}` : ""}` });
    setStages(outcome.kind === "queued" ? { job: outcome.job } : null);
    reload?.();
  };

  const confirm = async () => {
    if (!canConfirm) return;
    setBusy(true);
    setMessage(null);
    const result = await createProjectTitleAnnulments(projectId, {
      items: toSend.map((title) => ({
        legalEntityId: title.legalEntityId ?? (entityValue !== "" ? Number(entityValue) : undefined),
        billId: title.billId,
      })),
      category,
      reason: reason.trim(),
      applyNow: true,
    });
    setBusy(false);
    if (!result?.ok) {
      setMessage({ tone: "error", text: errorText(result) });
      return;
    }
    const created = Array.isArray(result.body?.created) ? result.body.created.length : toSend.length;
    finish(result.body, `${titleCount(created)} ${created === 1 ? "anulado" : "anulados"} na Ampli.`);
    setPreview(null);
    setNumbers("");
    setReason("");
  };

  const revoke = async (event) => {
    event.preventDefault();
    if (!revoking || !validReason(revoking.reason) || busy) return;
    setBusy(true);
    setMessage(null);
    const result = await revokeProjectTitleAnnulment(projectId, revoking.item.id, {
      revokeReason: revoking.reason.trim(),
      applyNow: true,
    });
    setBusy(false);
    if (!result?.ok) {
      setMessage({ tone: "error", text: errorText(result) });
      return;
    }
    finish(result.body, `Título ${revoking.item.titleNumber ?? revoking.item.billId} reincluído.`);
    setRevoking(null);
  };

  if (!open) return null;

  const numberHeader = `Nº ${erpLabel(erp)}`;
  const columns = canWrite ? 9 : 8;

  return (
    <div className="offcanvas active">
      <div
        ref={dialogRef}
        tabIndex={-1}
        onKeyDown={trapTab}
        className="offcanvas-container large cost-origin-titles-surface"
        role="dialog"
        aria-modal="true"
        aria-label="Títulos anulados na Ampli"
      >
        <hgroup className="offcanvas-header">
          <h2>Títulos anulados na Ampli</h2>
          <button onClick={onClose} aria-label="Fechar">
            <i className="icon icon-width-24 icon-times background-accent" aria-hidden={true}></i>
          </button>
        </hgroup>
        <div className="offcanvas-content">
          <p className="card-label margin-top-8" data-testid="annulments-summary">
            {`${titleCount(totals.count)} · A pagar ${brl(totals.toPay)} · Pago ${brl(totals.paid)} · Total ${brl(totals.total)}`}
          </p>

          {status === "loading" && !data && <p className="card-label margin-top-16">Carregando…</p>}
          {status === "error" && !data && (
            <p className="empty-state margin-top-16">Não foi possível carregar os títulos anulados. Tente novamente.</p>
          )}

          {pending && (
            <div className="alert alert-info margin-top-16" role="note">
              <h3 className="info-title">Atualização do financeiro pendente</h3>
              <p>{`${PENDING_BANNER_TEXT}. Os valores só refletem a lista depois do próximo processamento financeiro.`}</p>
            </div>
          )}

          {message && (
            <div className={`alert alert-${message.tone === "error" ? "error" : "success"} margin-top-16`} role="status" aria-live="polite">
              <p>{message.text}</p>
            </div>
          )}

          {stages && (
            <UpdateJobStages
              initialJob={stages.job}
              fetchJob={async () => {
                const res = await getUpdateJob?.(project?.building_id);
                return { ok: !!res?.ok, job: res?.body?.job ?? null };
              }}
              onTerminal={(job) => {
                // Sem recarregar a página: um segundo lote em preenchimento não se perde.
                if (job?.status === "succeeded") refreshAfterJob();
              }}
            />
          )}

          {data && !canWrite && data.selfService === false && (
            <p className="card-label margin-top-16" role="note">
              A anulação de títulos pelo Ampli Obras não está habilitada para esta construtora. A lista aparece só para consulta.
            </p>
          )}

          {canWrite && (
            <section className="margin-top-24" aria-label="Anular títulos">
              <h3>Anular títulos</h3>
              <form onSubmit={lookup}>
                <label className="card-label" htmlFor="annulments-numbers">
                  {`Números dos títulos (${mega ? "AP ou documento" : "Sienge"}), separados por vírgula, espaço ou quebra de linha`}
                </label>
                <textarea
                  id="annulments-numbers"
                  rows={3}
                  value={numbers}
                  onChange={(event) => { setNumbers(event.target.value); setPreview(null); }}
                  style={{ width: "100%" }}
                />
                {tooMany && (
                  <p role="alert" className="empty-state">{`O limite é de ${MAX_NUMBERS} números por vez.`}</p>
                )}
                <div className="flex gap-8 margin-top-8" style={{ alignItems: "center" }}>
                  <label className="card-label" htmlFor="annulments-entity">{mega ? "Filial no MEGA" : "Empresa no Sienge"}</label>
                  {legalEntities.length > 0 ? (
                    <select
                      id="annulments-entity"
                      value={entityValue}
                      onChange={(event) => { setLegalEntityId(event.target.value); setPreview(null); }}
                    >
                      {entityValue === "" && <option value="">Escolha a empresa</option>}
                      {legalEntities.map((entity) => (
                        <option key={entity.id} value={String(entity.id)}>{entity.name ?? entity.id}</option>
                      ))}
                    </select>
                  ) : (
                    <input
                      id="annulments-entity"
                      inputMode="numeric"
                      value={entityValue}
                      onChange={(event) => { setLegalEntityId(event.target.value); setPreview(null); }}
                    />
                  )}
                  <button
                    type="submit"
                    className="button button-accent small"
                    disabled={busy || parsed.length === 0 || tooMany}
                  >
                    Ver títulos
                  </button>
                </div>
              </form>

              {preview && (
                <div className="margin-top-16">
                  <div className="cost-origin-titles-table">
                    <table aria-label="Prévia dos títulos">
                      <thead>
                        <tr>
                          <th style={th}></th>
                          <th style={th}>Número</th>
                          <th style={th}>Situação</th>
                          <th style={th}>Credor</th>
                          <th style={th}>Documento</th>
                          <th style={thR}>A pagar</th>
                          <th style={thR}>Pago</th>
                          <th style={thR}>Total</th>
                          <th style={th}>Obras</th>
                        </tr>
                      </thead>
                      <tbody>
                        {results.map((row, index) => {
                          const ambiguous = row.status === "ambiguous";
                          const candidates = Array.isArray(row.candidates) ? row.candidates : [];
                          const title = row.status === "found"
                            ? row.title ?? null
                            : ambiguous ? ambiguousTitle(row, index, candidates[Number(chosen[index])]) : null;
                          const shown = title ?? {};
                          const toPay = Number(shown.toPay) || 0;
                          const paid = Number(shown.paid) || 0;
                          return (
                            <tr key={`${row.input}-${index}`}>
                              <td style={td}>
                                {(row.status === "found" || (ambiguous && chosen[index] !== undefined && chosen[index] !== "")) && (
                                  <input
                                    type="checkbox"
                                    aria-label={`Incluir ${row.input}`}
                                    checked={included[index] !== false}
                                    onChange={(event) => setIncluded((current) => ({ ...current, [index]: event.target.checked }))}
                                  />
                                )}
                              </td>
                              <td style={{ ...td, fontWeight: 600 }}>{row.input}</td>
                              <td style={td}>
                                {STATUS_LABELS[row.status] || row.status}
                                {ambiguous && (
                                  <select
                                    aria-label={`Empresa de ${row.input}`}
                                    value={chosen[index] ?? ""}
                                    onChange={(event) => pickCandidate(row, index, event.target.value)}
                                  >
                                    <option value="">Escolha</option>
                                    {candidates.map((candidate, candidateIndex) => (
                                      <option key={`${candidate.legalEntityId}-${candidate.billId}-${candidateIndex}`} value={String(candidateIndex)}>
                                        {candidateLabel(candidate)}
                                      </option>
                                    ))}
                                  </select>
                                )}
                                {(Array.isArray(shown.warnings) ? shown.warnings : []).map((warning) => (
                                  <p key={warning} className="card-label" role="note">{warning}</p>
                                ))}
                              </td>
                              <td style={td}>{shown.creditorName || "—"}</td>
                              <td style={td}>{shown.documentType || "—"}</td>
                              <td style={num}>{title ? brl(toPay) : "—"}</td>
                              <td style={num}>{title ? brl(paid) : "—"}</td>
                              <td style={num}>{title ? brl(shown.total ?? toPay + paid) : "—"}</td>
                              <td style={td}>{buildingList(shown) || "—"}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                  <div className="flex gap-8 margin-top-16" style={{ alignItems: "center", flexWrap: "wrap" }}>
                    <label className="card-label" htmlFor="annulments-category">Categoria</label>
                    <select id="annulments-category" value={category} onChange={(event) => setCategory(event.target.value)}>
                      {Object.entries(CATEGORY_LABELS).map(([value, label]) => <option key={value} value={value}>{label}</option>)}
                    </select>
                  </div>
                  <label className="card-label margin-top-8" htmlFor="annulments-reason">Motivo</label>
                  <textarea
                    id="annulments-reason"
                    rows={2}
                    maxLength={REASON_MAX}
                    value={reason}
                    onChange={(event) => setReason(event.target.value)}
                    style={{ width: "100%" }}
                  />
                  <p className="card-label">{`Entre ${REASON_MIN} e ${REASON_MAX} caracteres.`}</p>
                  {reason.trim().length > 0 && !reasonOk && (
                    <p role="alert" className="empty-state">{`Informe um motivo de no mínimo ${REASON_MIN} caracteres.`}</p>
                  )}
                  <p className="card-label">Vale também para a Diretoria. O ERP não muda; os números só mudam quando o financeiro é atualizado.</p>
                  <button type="button" className="button button-accent small" disabled={!canConfirm} onClick={confirm}>
                    Anular e atualizar financeiro
                  </button>
                </div>
              )}
            </section>
          )}

          <section className="margin-top-24" aria-label="Títulos anulados">
            <div className="cost-origin-titles-table">
              <table>
                <thead>
                  <tr>
                    <th style={th}>Credor</th>
                    <th style={th}>Documento</th>
                    <th style={th}>{numberHeader}</th>
                    <th style={thR}>A pagar</th>
                    <th style={thR}>Pago</th>
                    <th style={thR}>Total</th>
                    <th style={th}>Motivo</th>
                    <th style={th}>Anulado por</th>
                    {canWrite && <th style={th}></th>}
                  </tr>
                </thead>
                <tbody>
                  {items.map((item) => {
                    const t = itemTotals(item);
                    // Título que não atinge esta obra (thisBuilding nulo): sem valores daqui.
                    const other = item.affectsThisBuilding === false;
                    return (
                      <tr key={item.id}>
                        <td style={td}>
                          {item.creditorName || "—"}
                          {other && <p className="card-label" role="note">Atinge outras obras</p>}
                        </td>
                        <td style={td}>{item.documentType || "—"}</td>
                        <td style={{ ...td, fontWeight: 600, fontVariantNumeric: "tabular-nums" }}>{numberCell(item, mega)}</td>
                        <td style={num}>{other ? "—" : brl(t.toPay)}</td>
                        <td style={num}>{other ? "—" : brl(t.paid)}</td>
                        <td style={num}>{other ? "—" : brl(t.total)}</td>
                        <td style={td}>
                          <strong>{CATEGORY_LABELS[item.category] || item.category || ""}</strong>
                          {item.reason ? <><br />{item.reason}</> : null}
                        </td>
                        <td style={td}>
                          {`${personOf(item.createdBy)}, ${civil(item.createdAt)}`}
                          {ORIGIN_LABELS[item.createdVia] ? <><br />{ORIGIN_LABELS[item.createdVia]}</> : null}
                        </td>
                        {canWrite && (
                          <td style={{ ...td, whiteSpace: "nowrap" }}>
                            <button
                              type="button"
                              className="button button-accent small"
                              disabled={busy}
                              onClick={() => setRevoking({ item, reason: "" })}
                            >
                              Reincluir
                            </button>
                          </td>
                        )}
                      </tr>
                    );
                  })}
                  {items.length === 0 && status !== "loading" && (
                    <tr><td style={td} colSpan={columns}>Nenhum título anulado.</td></tr>
                  )}
                </tbody>
              </table>
            </div>

            {canWrite && revoking && (
              <form className="margin-top-16" onSubmit={revoke} aria-label="Reincluir título">
                <h3>{`Reincluir o título ${revoking.item.titleNumber ?? revoking.item.billId}?`}</h3>
                <label className="card-label" htmlFor="annulments-revoke-reason">Motivo da reinclusão</label>
                <textarea
                  id="annulments-revoke-reason"
                  rows={2}
                  maxLength={REASON_MAX}
                  value={revoking.reason}
                  onChange={(event) => setRevoking((current) => ({ ...current, reason: event.target.value }))}
                  style={{ width: "100%" }}
                />
                <p className="card-label">{`Entre ${REASON_MIN} e ${REASON_MAX} caracteres.`}</p>
                <div className="flex gap-8">
                  <button type="submit" className="button button-accent small" disabled={busy || !validReason(revoking.reason)}>
                    Confirmar reinclusão e atualizar financeiro
                  </button>
                  <button type="button" className="button small" disabled={busy} onClick={() => setRevoking(null)}>Cancelar</button>
                </div>
              </form>
            )}
          </section>

          {revoked.length > 0 && (
            <details className="margin-top-24">
              <summary>{`Reincluídos (${revoked.length})`}</summary>
              <ul>
                {revoked.map((item) => (
                  <li key={item.id}>
                    {`Título ${item.titleNumber ?? item.billId}: anulado por ${personOf(item.createdBy)} em ${civil(item.createdAt)}, reincluído por ${personOf(item.revokedBy)} em ${civil(item.revokedAt)}. Motivo da anulação: ${item.reason ?? "—"}.${item.revokeReason ? ` Motivo da reinclusão: ${item.revokeReason}.` : ""}`}
                  </li>
                ))}
              </ul>
            </details>
          )}
        </div>
      </div>
      <div onClick={onClose} className="offcanvas-background" />
    </div>
  );
};

export default TitleAnnulmentsOffcanvas;
